import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";

// Scale geometry constants
const STEPS = [1, 2, 3, 4, 5];
const START_PERCENT = 25;
const END_PERCENT = 75;
const SCALE_RANGE_PERCENT = END_PERCENT - START_PERCENT; // 50%
const STEP_INTERVAL_PERCENT = SCALE_RANGE_PERCENT / (STEPS.length - 1); // 12.5%

/**
 * Calculates thumb position percentage for a discrete step (1..5)
 * Step 1 -> 25%, Step 2 -> 37.5%, Step 3 -> 50%, Step 4 -> 62.5%, Step 5 -> 75%
 */
const getThumbPercent = (step) =>
    START_PERCENT + (step - 1) * STEP_INTERVAL_PERCENT;

/**
 * Modern Functional LikertScale Component
 */
const LikertScale = ({
    value = 3,
    onChange = () => {},
    textColor = "#ffffff",
    leftLabel = "Strongly disagree",
    middleLabel = "",
    rightLabel = "Strongly Agree",
}) => {
    const [selectedValue, setSelectedValue] = useState(value);
    const [isDragging, setIsDragging] = useState(false);
    const trackRef = useRef(null);

    // Sync state when external prop value changes
    useEffect(() => {
        if (value !== undefined) {
            setSelectedValue(value);
        }
    }, [value]);

    const updateStep = useCallback(
        (step) => {
            const clampedVal = Math.max(1, Math.min(STEPS.length, step));
            setSelectedValue((prevVal) => {
                if (prevVal !== clampedVal) {
                    onChange(clampedVal);
                }
                return clampedVal;
            });
        },
        [onChange],
    );

    /**
     * Converts client X coordinate into discrete step index (1..5)
     */
    const getStepFromClientX = useCallback((clientX) => {
        if (!trackRef.current) return 3;
        const rect = trackRef.current.getBoundingClientRect();
        const clickX = clientX - rect.left;
        const ratio = Math.max(0, Math.min(1, clickX / rect.width));

        // Map ratio from 25%..75% range to 0..1 scale ratio
        const scaleRatio = Math.max(
            0,
            Math.min(
                1,
                (ratio - START_PERCENT / 100) / (SCALE_RANGE_PERCENT / 100),
            ),
        );
        return Math.round(scaleRatio * (STEPS.length - 1)) + 1;
    }, []);

    const handlePointerDown = (e) => {
        setIsDragging(true);
        const step = getStepFromClientX(e.clientX);
        updateStep(step);
    };

    // Window event listeners active during drag
    useEffect(() => {
        if (!isDragging) return;

        const handlePointerMove = (e) => {
            const step = getStepFromClientX(e.clientX);
            updateStep(step);
        };

        const handlePointerUp = () => {
            setIsDragging(false);
        };

        window.addEventListener("pointermove", handlePointerMove);
        window.addEventListener("pointerup", handlePointerUp);
        window.addEventListener("pointercancel", handlePointerUp);

        return () => {
            window.removeEventListener("pointermove", handlePointerMove);
            window.removeEventListener("pointerup", handlePointerUp);
            window.removeEventListener("pointercancel", handlePointerUp);
        };
    }, [isDragging, getStepFromClientX, updateStep]);

    const thumbPercent = getThumbPercent(selectedValue);

    return (
        <div
            className={`likert-scale ${isDragging ? "is-dragging" : ""}`}
            style={{ color: textColor }}
        >
            {/* Header Labels (Positioned above tick 1 & tick 5) */}
            <div className="likert-scale__labels-header">
                <div className="likert-scale__label-cell likert-scale__label-cell--left">
                    <span className="likert-scale__label-text">
                        {leftLabel}
                    </span>
                </div>
                <div className="likert-scale__label-cell likert-scale__label-cell--right">
                    <span className="likert-scale__label-text">
                        {rightLabel}
                    </span>
                </div>
            </div>

            {/* Track Wrapper */}
            <div
                className="likert-scale__track-wrapper"
                ref={trackRef}
                onPointerDown={handlePointerDown}
            >
                {/* Full-width horizontal line */}
                <div className="likert-scale__line" />

                {/* 5 Discrete Tick Points */}
                <div className="likert-scale__ticks">
                    {STEPS.map((step) => {
                        const isSelected = selectedValue === step;
                        const tickPercent = getThumbPercent(step);
                        return (
                            <button
                                key={step}
                                type="button"
                                className={`likert-scale__tick-btn ${
                                    isSelected ? "is-selected" : ""
                                }`}
                                style={{ left: `${tickPercent}%` }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    updateStep(step);
                                }}
                                aria-label={`Scale point ${step}`}
                            >
                                <span className="likert-scale__tick-vline" />
                                <span className="likert-scale__tick-dashed-circle">
                                    <span className="likert-scale__tick-dot" />
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* Sliding Thumb Handle */}
                <div
                    className="likert-scale__thumb"
                    style={{ left: `${thumbPercent}%` }}
                >
                    <svg
                        width="10"
                        height="12"
                        viewBox="0 0 10 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M3 2.5L1 6L3 9.5M7 2.5L9 6L7 9.5"
                            stroke="#888888"
                            strokeWidth="1.75"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </div>

            {/* Footer Label (Positioned below middle tick) */}
            {middleLabel && (
                <div className="likert-scale__labels-footer">
                    <div className="likert-scale__label-cell likert-scale__label-cell--middle">
                        <span className="likert-scale__label-text">
                            {middleLabel}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

LikertScale.propTypes = {
    value: PropTypes.number,
    onChange: PropTypes.func,
    textColor: PropTypes.string,
    leftLabel: PropTypes.string,
    middleLabel: PropTypes.string,
    rightLabel: PropTypes.string,
};

export default LikertScale;
